/**
 * Модалка входа / регистрации. Имеет два таба, общую форму email+пароль
 * (+ опциональное имя на регистрации). По успеху коллбек onSuccess -
 * родитель решает, куда вести юзера. Дизайн повторяет PromoDialog /
 * BuyPackDialog / RenameChatDialog - общая Radix-обвязка из ui/Dialog.
 */
import { useEffect, useState } from "react";
import { Loader2, Mail, Lock, UserRound, MailCheck } from "lucide-react";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useGuestLogin, useLogin, useRequestPasswordReset, useSignup } from "@/hooks/useAuthFlow";
import { ApiHttpError } from "@/lib/api";
import { cn } from "@/lib/cn";

type Mode = "login" | "signup" | "forgot";

interface AuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultMode?: Mode;
  /** Куда ушёл юзер после успешного входа - родитель решит. */
  onSuccess?: () => void;
  /** Опциональный заголовок (например, «Войдите, чтобы открыть каталог»). */
  title?: string;
  subtitle?: string;
}

const ERROR_LOOKUP: Record<string, string> = {
  email_already_registered: "Этот email уже зарегистрирован",
  invalid_credentials:      "Неверный email или пароль",
  registration_disabled:    "Регистрация временно недоступна",
  access_restricted:        "Вход открыт только для администраторов",
  guest_limit_reached:      "Достигнут лимит гостевых входов с вашего адреса. Зарегистрируйтесь, чтобы продолжить",
  rate_limited:             "Слишком много попыток. Подождите минуту и попробуйте снова",
  http_error:               "Не удалось связаться с сервером",
};

function humanError(code: string): string {
  return ERROR_LOOKUP[code] || "Что-то пошло не так. Попробуйте ещё раз";
}

export function AuthDialog({
  open, onOpenChange, defaultMode = "login", onSuccess,
  title, subtitle,
}: AuthDialogProps) {
  const [mode, setMode] = useState<Mode>(defaultMode);
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [name, setName]         = useState("");
  const [error, setError]       = useState<string | null>(null);

  const signup = useSignup();
  const login  = useLogin();
  const forgot = useRequestPasswordReset();
  const guest  = useGuestLogin();
  const [forgotSent, setForgotSent] = useState(false);
  // Email, на который отправлена ссылка подтверждения после регистрации.
  // Не null → показываем экран «проверьте почту» вместо формы.
  const [signupSentTo, setSignupSentTo] = useState<string | null>(null);
  const pending = signup.isPending || login.isPending || forgot.isPending || guest.isPending;

  async function guestLogin() {
    setError(null);
    try {
      await guest.mutateAsync();
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      const code = e instanceof ApiHttpError ? e.code : "http_error";
      setError(humanError(code));
    }
  }

  // Сбрасываем форму/состояние при каждом открытии/смене режима
  useEffect(() => {
    if (open) { setError(null); setForgotSent(false); setSignupSentTo(null); }
  }, [open, mode]);
  useEffect(() => { if (open) setMode(defaultMode); }, [open, defaultMode]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (mode === "signup") {
        const res = await signup.mutateAsync({
          email: email.trim(),
          password,
          display_name: name.trim() || undefined,
        });
        // Mailer-on ветка: backend не логинит, пользователь должен
        // подтвердить email ссылкой из письма (Resend). Показываем экран
        // «проверьте почту» вместо формы — иначе диалог тихо висел бы
        // без обратной связи.
        if (res.requires_confirmation) {
          setSignupSentTo(email.trim());
          return;
        }
      } else if (mode === "forgot") {
        await forgot.mutateAsync({ email: email.trim() });
        setForgotSent(true);
        return;
      } else {
        await login.mutateAsync({ email: email.trim(), password });
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      const code = e instanceof ApiHttpError ? e.code : "http_error";
      setError(humanError(code));
    }
  }

  const heading = title
    ?? (mode === "signup" ? "Создайте аккаунт"
      : mode === "forgot" ? "Сброс пароля"
      : "Войдите в SpectrAI");
  const sub = subtitle
    ?? (mode === "signup" ? "Email и пароль. Получите стартовый бонус на счёт"
      : mode === "forgot" ? "Введите email - пришлём ссылку для сброса пароля"
      : "Войдите, чтобы продолжить");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        {signupSentTo ? (
          /* Экран после регистрации: аккаунт создан, ждём подтверждения email. */
          <div className="text-center">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-emerald-50 text-emerald-600">
              <MailCheck className="h-6 w-6" />
            </div>
            <DialogTitle className="mt-3">Аккаунт создан</DialogTitle>
            <DialogDescription className="mt-1">
              Мы отправили письмо со ссылкой для подтверждения на{" "}
              <span className="font-medium text-ink">{signupSentTo}</span>. Перейдите по ссылке, чтобы войти
            </DialogDescription>
            <div className="mt-5 rounded-xl border border-line-soft bg-surface-2/50 px-4 py-3 text-left text-xs text-ink-mute">
              Не пришло письмо? Проверьте папку «Спам» или попробуйте зарегистрироваться ещё раз через пару минут
            </div>
            <div className="mt-5 flex flex-col gap-2">
              <Button size="lg" onClick={() => onOpenChange(false)}>Понятно</Button>
              <button
                type="button"
                className="text-xs text-brand-700 hover:underline"
                onClick={() => { setSignupSentTo(null); setMode("login"); }}
              >
                Вернуться ко входу
              </button>
            </div>
          </div>
        ) : (
        <>
        <DialogTitle>{heading}</DialogTitle>
        <DialogDescription>{sub}</DialogDescription>

        {/* tab pills - скрываем когда юзер в forgot-режиме */}
        {mode !== "forgot" && (
          <div className="mt-4 inline-flex rounded-full bg-surface-2 p-1 text-sm">
            <button
              type="button"
              onClick={() => setMode("login")}
              className={cn(
                "rounded-full px-4 py-1.5 transition",
                mode === "login" ? "bg-surface shadow-soft text-ink" : "text-ink-mute hover:text-ink",
              )}
            >
              Вход
            </button>
            <button
              type="button"
              onClick={() => setMode("signup")}
              className={cn(
                "rounded-full px-4 py-1.5 transition",
                mode === "signup" ? "bg-surface shadow-soft text-ink" : "text-ink-mute hover:text-ink",
              )}
            >
              Регистрация
            </button>
          </div>
        )}

        <form onSubmit={submit} className="mt-4 space-y-2.5">
          {mode === "signup" && (
            <Field label="Имя" icon={<UserRound className="h-4 w-4" />}>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Как к вам обращаться"
                maxLength={80}
                autoComplete="name"
              />
            </Field>
          )}

          <Field label="Email" icon={<Mail className="h-4 w-4" />}>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </Field>

          {mode !== "forgot" && (
            <Field
              label="Пароль"
              icon={<Lock className="h-4 w-4" />}
              hint={mode === "signup" ? "Минимум 8 символов" : undefined}
            >
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                minLength={mode === "signup" ? 8 : 1}
                required
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
              />
            </Field>
          )}

          {mode === "login" && (
            <div className="text-right">
              <button
                type="button"
                className="text-xs text-brand-700 hover:underline"
                onClick={() => setMode("forgot")}
              >
                Забыли пароль?
              </button>
            </div>
          )}

          {forgotSent && mode === "forgot" && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
              Если такой email зарегистрирован - мы прислали письмо со ссылкой для сброса
              Ссылка действительна 1 час
            </div>
          )}

          {error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {error}
            </div>
          )}

          <Button type="submit" size="md" className="w-full gap-2 lg:h-12 lg:text-base" disabled={pending}>
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === "signup" ? "Зарегистрироваться"
              : mode === "forgot" ? "Отправить ссылку"
              : "Войти"}
          </Button>

          {mode !== "forgot" && (
            <>
              <div className="flex items-center gap-3 py-1 text-[11px] text-ink-soft">
                <span className="h-px flex-1 bg-line-soft" />
                или
                <span className="h-px flex-1 bg-line-soft" />
              </div>
              <Button
                type="button"
                variant="secondary"
                size="md"
                className="w-full gap-2 lg:h-12 lg:text-base"
                disabled={pending}
                onClick={guestLogin}
              >
                {guest.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Войти как гость
              </Button>
            </>
          )}

          <div className="text-center text-xs text-ink-soft pt-1">
            {mode === "signup" ? (
              <>Уже есть аккаунт?{" "}
                <button type="button" className="text-brand-700 hover:underline" onClick={() => setMode("login")}>
                  Войти
                </button>
              </>
            ) : mode === "forgot" ? (
              <>Вспомнили пароль?{" "}
                <button type="button" className="text-brand-700 hover:underline" onClick={() => setMode("login")}>
                  Войти
                </button>
              </>
            ) : (
              <>Ещё нет аккаунта?{" "}
                <button type="button" className="text-brand-700 hover:underline" onClick={() => setMode("signup")}>
                  Создать
                </button>
              </>
            )}
          </div>
        </form>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label, icon, hint, children,
}: { label: string; icon: React.ReactNode; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-mute">
        <span className="text-ink-soft">{icon}</span>
        {label}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-ink-soft">{hint}</span>}
    </label>
  );
}
